import { LayoutService } from '../../../../shared/services/layout.service';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { GridsterConfig } from 'angular-gridster2';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { Layout } from '../../../../shared/models/layout';
import { Subject } from '../../../../shared/models/subject';
import { SubjectSettingService } from '../../../../shared/services/subject-setting.service';
import { WidgetService } from '../../../widgets/widget.service';
import { SubjectSetting } from '../../../../shared/models/subject-setting';
import { LayoutItem, Template } from 'app/shared/models/template';
import { ReplaySubject, Subscription } from 'rxjs';
import { Grid } from 'app/shared/helpers/grid';
import { UserService } from 'app/shared/services/user.service';
import { DefaultDataService } from 'app/shared/services/default-data.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { StatusService } from 'app/shared/services/status.service';
import { MatDialog } from '@angular/material/dialog';
import { RetentionPromptComponent } from './retention-prompt/retention-prompt.component';

@AutoUnsubscribe()
@Component({
  selector: 'subject-edit',
  templateUrl: './subject-edit.component.html',
  styleUrls: ['./subject-edit.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SubjectEditComponent implements OnInit, OnDestroy {
  _subject: Subject;
  subjectSetting: SubjectSetting;

  layout: Layout;
  grid = new Grid();
  options: GridsterConfig = {};
  template: Template;
  categoryItem: LayoutItem;
  statusItem: LayoutItem;

  valueChanged: ReplaySubject<any> = new ReplaySubject<any>(1, 100);
  onValueChanged: Subscription;
  onItemChanged: Subscription;

  @Input() set subject(val: Subject) {
    this._subject = new Subject(val);

    // get the subject settings so we can check for autogenerate when building the form
    this.subjectSettingsService
      .getBySubjectTypeId(this._subject.subjectTypeId)
      .subscribe((setting) => {
        this.subjectSetting = setting;

        // now build layout
        this.getLayout();
      });
  }

  get subject() {
    return this._subject;
  }

  // size is set by the template...
  // make size accessible to encapsulating components
  @Input() sizeX = 630;
  @Input() sizeY = 522;

  @Output() onChanged: EventEmitter<any> = new EventEmitter();
  @Output() onLayoutChanged: EventEmitter<any> = new EventEmitter();

  public get gridItems(): any[] {
    if (this.grid && this.grid.items) return this.grid?.items;
    else return [];
  }

  // value changed in one of the widgets, set 'hasChanged' flag and update the widget
  handleValueChanged = {
    valueEmitter: (dataItem) => {
      this.valueChanged.next(dataItem);
    },
  };

  constructor(
    private subjectSettingsService: SubjectSettingService,
    private defaultDataService: DefaultDataService,
    private layoutService: LayoutService,
    private widgetService: WidgetService,
    private userService: UserService,
    private changeDetector: ChangeDetectorRef,
    private statusService: StatusService,
    public dialog: MatDialog
  ) {
    this.grid = new Grid();
    this.grid.items = [];
    this.grid.values = [];
  }

  ngOnInit() {
    this.onItemChanged = this.grid.onValueChanged.subscribe((dataItem) => {
      const currentCategory = this._subject.categoryId;
      const categoryChanged =
        this.categoryItem &&
        dataItem.id === this.categoryItem.id &&
        (this._subject.categoryId || -1) !== dataItem.value;
      const statusChanged =
        this.statusItem &&
        dataItem.id === this.statusItem.id &&
        (this._subject.categoryId || -1) !== dataItem.value;
      const currentUserId =
        this._subject.subjectUsers && this._subject.subjectUsers.length > 0
          ? this._subject.subjectUsers[0].userId
          : 0;

      // make sure that the widget metadata persists to the subject
      if (this._subject) {
        this._subject.setFromMetadata(this.layout, this.grid.values);

        // update any calculated fields...
        this.layout.subjectType.dataFields
          .filter((i) => i.dataType.toLowerCase() === 'calculated')
          .forEach((datafield) => {
            const item = this.template.layoutItems.find(
              (i) => i.name.toLowerCase() === datafield.name.toLowerCase()
            );
            if (item) {
              const gridItem = this.grid.items.find((i) => i.id === item.id);
              if (
                gridItem &&
                gridItem.metadata.value !==
                  this._subject.subjectData.data[datafield.id]
              ) {
                gridItem.metadata.value =
                  this._subject.subjectData.data[datafield.id];
              }
            }
          });
      }

      // if the userid has changed consider whether we need to update the managerId also...
      if (this.defaultDataService.autoSetManager) {
        const userItem = this.template.layoutItems.find(
          (i) => i.name.toLowerCase() === 'userid'
        );
        if (userItem && dataItem.id === userItem.id) {
          const userId =
            this._subject.subjectUsers &&
            this._subject.subjectUsers.length > 0 &&
            this._subject.subjectUsers[0].userId
              ? this._subject.subjectUsers[0].userId
              : 0;
          if (userId > 0 && userId !== currentUserId) {
            // user has changed - set manager?
            const managerId =
              this._subject.subjectUsers &&
              this._subject.subjectUsers.length > 0 &&
              this._subject.subjectUsers[0].managerId
                ? this._subject.subjectUsers[0].managerId
                : 0;
            if (managerId < 1) {
              // set managerId to specified user's manager
              const user = this.userService.getUserById(userId);
              if (user && user.managerId) {
                const item = this.template.layoutItems.find(
                  (i) => i.name.toLowerCase() === 'managerid'
                );
                if (item) {
                  this.grid.updateItem({
                    id: item.id,
                    value: user.managerId,
                    valid: true,
                  });
                }
              }
            }
          }
        }
      }
      if (statusChanged) {
        this.statusService
          .getById(+dataItem.value.replace(/[^0-9]+/g, ''))
          .subscribe((resultData) => {
            if (
              !resultData.promptForRetention
              // && this._subject.retentionDate
            ) {
              // PJT - If retention is not enabled for new subject status, then clear retention data from subject
              this._subject.retentionDate = null;
              this._subject.retentionId = null;
            }
          });
      }

      // if has category and category is changed then we need to check the layout
      if (categoryChanged) {
        // MRD - because lookup values may change with category any change to category requires a reload of layout/widgets...
        // this.layoutService.getLayout(this._subject.subjectTypeId, dataItem.value)
        //   .pipe(take(1)).subscribe((response: Layout) => {
        //     if ((!currentCategory) || (this.layout && (response.id !== this.layout.id))){
        //       // layout change required, get layout
        //       this.getLayout();
        //       this.onChanged.emit({subject: this._subject, valid: this.grid.valid});
        //     }
        //   });
        // layout change required, get layout
        this.getLayout();
      }
      // Detect any changes.
      this.changeDetector.detectChanges();

      this.onChanged.emit({ subject: this._subject, valid: this.grid.valid });
    });

    this.onValueChanged = this.valueChanged
      .pipe(
        debounceTime(200), // 0.2 second debounce
        distinctUntilChanged()
      )
      .subscribe((dataItem) => {
        this.grid.updateItem(dataItem);
      });
  }

  ngOnDestroy() {
    this.onValueChanged?.unsubscribe();
    this.onItemChanged?.unsubscribe();
  }

  getLayout() {
    const categoryName = 'categoryid';
    const statusName = 'status';
    this.layoutService
      .getLayout(this._subject.subjectTypeId, this._subject.categoryId)
      .pipe(take(1))
      .subscribe((response: Layout) => {
        let layoutChanged =
          response && (!this.layout || response.id !== this.layout.id);
        this.layout = response;

        // force the layout to be updated with any changes to the subject type, such as colour
        this.layout.subjectType = undefined;
        this.layoutService
          .hydrate(this.layout)
          .pipe(take(1))
          .subscribe((hydratedLayout) => {
            this.layout = hydratedLayout;

            this.template =
              this._subject.isReferral && this.layout.referralTemplate.id
                ? this.layout.referralTemplate
                : this.layout.editTemplate;

            // set gridster options based on template size...
            const options = this.template.getSizeAndOptions(this.grid.size);
            layoutChanged =
              layoutChanged ||
              this.sizeX !== options.sizeX ||
              this.sizeY !== options.sizeY + 25;
            this.options = options.options;

            this.sizeX = options.sizeX;
            this.sizeY = options.sizeY + 25;
            if (layoutChanged) {
              this.onLayoutChanged.emit({
                layout: this.layout,
                sizeX: this.sizeX,
                sizeY: this.sizeY,
              });
            }

            setTimeout(() => {
              // setup category info
              this.categoryItem = this.template.layoutItems.find(
                (i) => i.name.toLowerCase() === categoryName
              );
              this.statusItem = this.template.layoutItems.find(
                (i) => i.name.toLowerCase() === statusName
              );
              const categoryInfo = this.grid.getCategoryInfo(
                categoryName,
                this._subject.categoryId,
                this.template,
                this.layout.subjectType
              );

              // add layout items and update with the subject metadata
              const autoGenerateReference =
                this.subjectSetting &&
                this.subjectSetting.autoGenerateReference;
              this.grid.items = [];
              this.grid.addItems(
                this._subject,
                this.widgetService,
                this.template,
                this.layout.subjectType,
                categoryInfo,
                autoGenerateReference
              );
              this.grid.values = this._subject.getMetadata(this.layout);
              this.changeDetector.detectChanges();
            }, 200);
          });
      });
  }

  // MRD - Don't add trackbyFn as this will stop layout updating properly when category is changed
  // trackByFn(index, item){
  //   return item.id;
  // }
}
